import { useEffect, useState } from 'react';

import { PathEditor } from '../curves/PathEditor.js';
import { applyEdits, profilePoints, sectionRing, type Vec2, type Vec3 } from '../curves/curve-math.js';
import '../curves/curves.css';
import { PropRow, SelectField } from '../../ui/controls.js';
import type { ShapeEditorProps } from './doc-context.js';
import { arr, Bool, bool, ListEditor, Num, num, obj, patch, Section, vec, Vec, type Obj } from './fields.js';
import { profileMode, ProfileSourceFields, resolvedProfile } from './profile-source.js';
import { points2 } from './LatheEditor.js';

/** Every section's ring in 3D, resolving "same as previous" like prism's `loft`. */
export function loftRings(sections: readonly Obj[], segments: number, smoothProfile: boolean): Vec3[][] {
  let current: Vec2[] | null = null;
  return sections.map((s) => {
    const p = resolvedProfile(s, segments, smoothProfile);
    if (p && p.length >= 3) current = p;
    const prof = current ?? profilePoints(undefined, undefined, undefined, 0.5, segments, false)!;
    return sectionRing(prof, vec(s.position, 3) ?? [0, 0, 0], vec(s.rotation, 3) ?? [0, 0, 0], vec(s.scale, 2) ?? [1, 1]);
  });
}

const MODE_LABEL: Record<string, string> = { profile: 'points', rect: 'rect', ellipse: 'ellipse', radius: 'circle', inherit: 'same as previous' };

/** Section rotations that lay the profiles across one axis, so the loft runs along it. */
const AXIS_ROTATION: Record<string, Vec3 | undefined> = { y: undefined, z: [90, 0, 0], x: [0, 0, 90] };
const AXIS_OPTIONS = [
  { value: 'y', label: 'Up (Y): vases, columns, trunks' },
  { value: 'z', label: 'Front to back (Z): car bodies, hulls' },
  { value: 'x', label: 'Side to side (X): wings, bars' },
];

/** The axis every section's rotation lays the loft along, or 'mixed'. */
export function loftAxis(sections: readonly Obj[]): string {
  const key = (s: Obj) => {
    const r = vec(s.rotation, 3) ?? [0, 0, 0];
    return Object.keys(AXIS_ROTATION).find((axis) => {
      const want = AXIS_ROTATION[axis] ?? [0, 0, 0];
      return want.every((v, i) => Math.abs(v - r[i]!) < 1e-6);
    });
  };
  const axes = new Set(sections.map(key));
  return axes.size === 1 ? ([...axes][0] ?? 'mixed') : sections.length ? 'mixed' : 'y';
}

const fmt = (n: number) => String(Math.round(n * 10000) / 10000);

/** One line per section: `x y z  rect w d r` / `ellipse rx rz` / `circle r` / `points` / `same`. */
export function sectionsToText(sections: readonly Obj[]): string {
  return sections
    .map((s, i) => {
      const p = vec(s.position, 3) ?? [0, 0, 0];
      const at = p.map(fmt).join(' ');
      const mode = profileMode(s, i === 0);
      const rect = vec(s.rect, 3);
      const ellipse = vec(s.ellipse, 2);
      const shape =
        mode === 'rect' && rect
          ? `rect ${rect.map(fmt).join(' ')}`
          : mode === 'ellipse' && ellipse
            ? `ellipse ${ellipse.map(fmt).join(' ')}`
            : mode === 'radius'
              ? `circle ${fmt(num(s.radius) ?? 0.5)}`
              : mode === 'profile'
                ? 'points'
                : 'same';
      return `${at}   ${shape}`;
    })
    .join('\n');
}

/**
 * Parses {@link sectionsToText} lines back into sections. Each line keeps the
 * rotation / scale (and for `points` the drawn profile) of the section it
 * replaces; new lines copy the previous line's rotation.
 */
export function parseSections(text: string, previous: readonly Obj[]): { sections: Obj[] } | { error: string } {
  const out: Obj[] = [];
  const lines = text.split(/\r?\n/);
  for (let n = 0; n < lines.length; n++) {
    const line = lines[n]!.replace(/#.*$/, '').replace(/[[\]()]/g, ' ').trim();
    if (!line) continue;
    const tokens = line.split(/[\s,;]+/).filter(Boolean);
    const pos = tokens.slice(0, 3).map(Number);
    if (pos.length < 3 || pos.some((v) => !Number.isFinite(v))) return { error: `Line ${n + 1}: start with the position x y z.` };
    const kind = (tokens[3] ?? 'same').toLowerCase();
    const args = tokens.slice(4).map(Number);
    if (args.some((v) => !Number.isFinite(v))) return { error: `Line ${n + 1}: "${kind}" takes numbers.` };
    const base = previous[out.length] ?? out[out.length - 1] ?? {};
    const section: Obj = {};
    if (base.rotation !== undefined) section.rotation = base.rotation;
    if (base.scale !== undefined && previous[out.length]) section.scale = base.scale;
    section.position = pos;
    if (kind === 'rect') {
      if (args.length < 2) return { error: `Line ${n + 1}: rect needs width, depth and optionally a corner radius.` };
      section.rect = [args[0]!, args[1]!, args[2] ?? 0];
    } else if (kind === 'ellipse') {
      if (args.length < 1) return { error: `Line ${n + 1}: ellipse needs one or two radii.` };
      section.ellipse = [args[0]!, args[1] ?? args[0]!];
    } else if (kind === 'circle' || kind === 'radius') {
      if (args.length < 1) return { error: `Line ${n + 1}: circle needs a radius.` };
      section.radius = args[0]!;
    } else if (kind === 'points') {
      const own = previous[out.length];
      if (!own || points2(own.profile).length < 3) return { error: `Line ${n + 1}: "points" keeps a section's drawn profile; this section has none.` };
      section.profile = own.profile;
    } else if (kind !== 'same') {
      return { error: `Line ${n + 1}: unknown profile "${tokens[3]}" (rect, ellipse, circle, points or same).` };
    }
    out.push(section);
  }
  if (out.length < 2) return { error: 'A loft needs at least 2 sections.' };
  return { sections: out };
}

/** Text box with every section; Apply (or Ctrl+Enter) replaces them all. */
function SectionsText({ sections, onApply }: { sections: readonly Obj[]; onApply: (sections: Obj[]) => void }) {
  const [text, setText] = useState(() => sectionsToText(sections));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(sectionsToText(sections)), [sections]);
  const apply = () => {
    const parsed = parseSections(text, sections);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);
    onApply(parsed.sections);
  };
  return (
    <div className="curve-text">
      <textarea
        className="mono"
        spellCheck={false}
        rows={Math.min(14, Math.max(4, sections.length + 1))}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            apply();
          }
        }}
        aria-label="Sections, one per line"
      />
      <div className="curve-text-row">
        {error ? (
          <span className="inline-error">{error}</span>
        ) : (
          <span className="muted">x y z, then rect w d corner · ellipse rx rz · circle r · same · Ctrl+Enter applies</span>
        )}
        <button type="button" className="ghost small" onClick={apply}>
          Apply
        </button>
      </div>
    </div>
  );
}

/** `loft`: a skin through cross-sections. */
export function LoftEditor({ shape, onChange }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  const sections = arr<unknown>(shape.sections).map((s) => obj(s) ?? {});
  const segments = num(shape.segments) ?? 24;
  const smoothProfile = bool(shape.smoothProfile) ?? false;
  const positions = sections.map((s) => (vec(s.position, 3) ?? [0, 0, 0]) as Vec3);
  const axis = loftAxis(sections);
  const [asText, setAsText] = useState(false);
  return (
    <>
      <PropRow label="Runs along" hint="Turns every section so the loft runs along this axis; place the sections along it (e.g. z from nose to tail).">
        <SelectField
          value={axis}
          options={axis === 'mixed' ? [...AXIS_OPTIONS, { value: 'mixed', label: 'Mixed (per-section rotation)' }] : AXIS_OPTIONS}
          onChange={(v) => {
            if (!(v in AXIS_ROTATION)) return;
            const rotation = AXIS_ROTATION[v];
            set('sections', sections.map((s) => patch(s, 'rotation', rotation)));
          }}
        />
      </PropRow>
      <Section title="Sections" hint="Section positions in three views; the dashed outlines are the section profiles. Click to add a section (it reuses the previous profile).">
        <div className="curve-text-row">
          <span className="muted">{sections.length} sections</span>
          <button type="button" className={`ghost small${asText ? ' is-active' : ''}`} onClick={() => setAsText(!asText)} title="Type every section as one line: position, then the profile">
            {asText ? 'Hide text' : 'Edit as text'}
          </button>
        </div>
        {asText && <SectionsText sections={sections} onApply={(next) => set('sections', next)} />}
        <PathEditor
          points={positions}
          closed={bool(shape.closed) ?? false}
          smooth={bool(shape.smooth) ?? true}
          samples={num(shape.samples) ?? 6}
          ghosts={loftRings(sections, Math.min(segments, 48), smoothProfile).map((ring) => ({ points: ring, closed: true }))}
          pointLabel={(i) => `Section ${i + 1}`}
          onChange={(pts, edits) => {
            const next = applyEdits(sections, edits, () => ({ position: [0, 0, 0] }) as Obj).map((s, i) =>
              pts[i] ? { ...s, position: pts[i] } : s,
            );
            set('sections', next);
          }}
        />
        <ListEditor
          title="Cross-sections"
          hint="Cross-sections in order; at least 2."
          items={sections}
          onChange={(next) => set('sections', next)}
          addLabel="Add section"
          make={(items) => {
            const last = items[items.length - 1];
            const p = (last && vec(last.position, 3)) ?? [0, 0, 0];
            return { position: [p[0]!, p[1]! + 0.25, p[2]!] };
          }}
          itemTitle={(s, i) => `Section ${i + 1} · ${MODE_LABEL[profileMode(s, i === 0)] ?? ''}`}
          render={(s, setSection, i) => (
            <SectionFields section={s} first={i === 0} segments={segments} smoothProfile={smoothProfile} onChange={setSection} />
          )}
        />
      </Section>
      <Num label="Segments" hint="Points generated for ellipse / circle / rect profiles." integer min={3} value={num(shape.segments)} fallback={24} onChange={(v) => set('segments', v)} />
      <Bool label="Smooth" hint="Smooth curve through the sections (not straight between them)." value={bool(shape.smooth)} fallback onChange={(v) => set('smooth', v)} />
      <Num label="Samples" hint="Curve samples between sections when smooth." integer min={1} max={64} value={num(shape.samples)} fallback={6} onChange={(v) => set('samples', v)} />
      <Bool label="Smooth profile" hint="Smooth closed curves through drawn profile points." value={bool(shape.smoothProfile)} onChange={(v) => set('smoothProfile', v)} />
      <Bool label="Caps" hint="Close the first and last sections." value={bool(shape.caps)} fallback onChange={(v) => set('caps', v)} />
      <Bool label="Closed" hint="Connect the last section back to the first (a ring)." value={bool(shape.closed)} onChange={(v) => set('closed', v)} />
    </>
  );
}

function SectionFields({
  section,
  first,
  segments,
  smoothProfile,
  onChange,
}: {
  section: Obj;
  first: boolean;
  segments: number;
  smoothProfile: boolean;
  onChange: (s: Obj) => void;
}) {
  const set = (key: string, v: unknown) => onChange(patch(section, key, v));
  return (
    <>
      <Vec label="Position" hint="Section center (part space)." value={vec(section.position, 3)} onChange={(v) => set('position', v)} />
      <Vec
        label="Rotation"
        hint="Euler XYZ degrees; the profile lies in the section's XZ plane."
        step={1}
        value={vec(section.rotation, 3)}
        onChange={(v) => set('rotation', v && v.every((n) => n === 0) ? undefined : v)}
      />
      <Vec
        label="Scale"
        hint="Profile scale along its x and z."
        size={2}
        step={0.05}
        value={vec(section.scale, 2) ?? [1, 1]}
        onChange={(v) => set('scale', v && v[0] === 1 && v[1] === 1 ? undefined : v)}
      />
      <ProfileSourceFields
        value={section}
        onChange={onChange}
        segments={segments}
        smooth={smoothProfile}
        axes={['X', 'Z']}
        defaultRadius={first ? 0.5 : undefined}
      />
    </>
  );
}
